import { useState, useEffect, useMemo } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@shared/ui/dialog";
import { Button } from "@shared/ui/button";
import { Input } from "@shared/ui/input";
import { Label } from "@shared/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@shared/ui/select";
import { Plus, X } from "lucide-react";
import { DynamicForm } from "@shared/components/DynamicForm";
import { FormField, FormService } from "@shared/services/FormServices";
import {
  useCreatePaymentMode,
  useFieldTypes,
  usePaymentFields,
  useUpdatePaymentMode,
} from "@features/cx/settings/hooks";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import { toast } from "@shared/ui/use-toast";
import { ContextualHelp } from "@shared/ui/contextual-help";

interface CustomField {
  key: string;
  label: string;
  fieldType: string;
}

interface PaymentMode {
  code: string;
  name: string;
  type: string;
  isActive: boolean;
  transactionFee?: number;
  remark?: string;
  minAmount?: number;
  maxAmount?: number;
  customFields?: CustomField[];
}

interface PaymentModeFormProps {
  isOpen: boolean;
  onClose: () => void;
  editingMode: PaymentMode | null;
  canEdit?: boolean;
}

const fieldTypeOptions = [
  { value: "text", label: "Text Field" },
  { value: "number", label: "Number Field" },
  { value: "date", label: "Date Field" },
];

export function PaymentModeForm({
  isOpen,
  onClose,
  editingMode,
  canEdit = true,
}: PaymentModeFormProps) {
  console.log("Editing Mode", editingMode);
  const remoteUtilityId = useRemoteUtilityId();
  const createPaymentMode = useCreatePaymentMode();
  const updatePaymentMode = useUpdatePaymentMode();
  const { data: fieldTypes } = useFieldTypes();
  const { data: paymentFields } = usePaymentFields();
  const [formData, setFormData] = useState({
    name: "",
    type: "",
    transactionFee: 0,
    remark: "",
    minAmount: 0,
    maxAmount: 0,
    customFields: [] as CustomField[],
  });

  const [newField, setNewField] = useState({
    label: "",
    fieldType: "text" as "text" | "number" | "date",
  });

  const getDefaultFields = useMemo(() => {
    if (paymentFields && Array.isArray(paymentFields)) {
      return paymentFields.map((field, index) => ({
        key: field.key || field.label || `default_field_${index}_${Date.now()}`,
        label: field.label,
        fieldType: field.fieldType,
      }));
    }
    return [];
  }, [paymentFields]);

  useEffect(() => {
    if (isOpen) {
      if (editingMode) {
        setFormData({
          name: editingMode.name,
          type: editingMode.type,
          transactionFee: editingMode.transactionFee || 0,
          remark: editingMode.remark || "",
          minAmount: editingMode.minAmount || 0,
          maxAmount: editingMode.maxAmount || 0,
          customFields:
            editingMode.customFields?.map((field) => ({
              key: field.key,
              label: field.label,
              fieldType: field.fieldType,
            })) || [],
        });
      } else {
        // For new payment modes, add default fields from API
        setFormData({
          name: "",
          type: "",
          transactionFee: 0,
          remark: "",
          minAmount: 0,
          maxAmount: 0,
          customFields: getDefaultFields,
        });
      }
      setNewField({
        label: "",
        fieldType: "text",
      });
    }
  }, [isOpen, editingMode, getDefaultFields]);

  const formFields: FormField[] = [
    {
      name: "name",
      type: "text",
      label: "Mode Name",
      placeholder: "Enter mode name",
      required: true,
      className: "col-span-2",
      disabled: Boolean(editingMode) || !canEdit,
      classes: {
        input: `${
          Boolean(editingMode)
            ? "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-gray-100 cursor-not-allowed"
            : "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        }`,
      },
      showHelperTooltip: true,
      tooltipText: "Enter a clear, descriptive name for this payment method (e.g., 'Credit Card', 'Cash', 'Digital Wallet'). This name will be displayed to users when selecting payment options."
    },
    {
      name: "remark",
      type: "textarea",
      label: "Description",
      disabled: !canEdit,
      placeholder: "Enter mode description",
      rows: 3,
      className: "col-span-2",
      showHelperTooltip: true,
      tooltipText: "Provide a detailed description of this payment method. Include any important information users should know, such as supported card types, processing times, or special requirements."
    },
  ];

  const form = FormService.createForm({
    fields: formFields,
    mode: "onChange",
    validateOnMount: false,
    defaultValues: formData,
  });

  useEffect(() => {
    form.reset(formData);
  }, [formData, form]);

  const handleFieldChange = (fieldName: string, value: any) => {
    setFormData((prev) => ({
      ...prev,
      [fieldName]: value,
    }));
  };

  const handleSubmit = () => {
    if (!formData.name.trim()) return;
    const operation = editingMode ? "update" : "create";
    switch (operation) {
      case "update":
        const payloadEdit = {
          name: formData.name,
          code: editingMode?.code,
          transaction_fee: formData.transactionFee,
          remark: formData.remark,
          custom_fields: formData.customFields,
          remote_utility_id: Number(remoteUtilityId),
        };
        updatePaymentMode.mutateAsync(payloadEdit);
        break;
      case "create":
        const payloadAdd = {
          name: formData.name,
          code: formData.name,
          remark: formData.remark,
          remote_utility_id: Number(remoteUtilityId),
          custom_fields: formData.customFields,
        };
        createPaymentMode.mutateAsync(payloadAdd);
        break;
    }
    handleClose();
  };

  const handleAddField = () => {
    if (!newField.label.trim()) {
      toast({ title: "Field label is required!" });
      return;
    }

    // Check for duplicates in current custom fields
    const existsInCustomFields = formData.customFields.some(
      (field) => field.label.toLowerCase() === newField.label.toLowerCase()
    );

    if (existsInCustomFields) {
      toast({ title: "Field with this name already exists!" });
      return;
    }

    const existsInDefaultFields =
      paymentFields && Array.isArray(paymentFields)
        ? paymentFields.some(
            (field) =>
              field.label?.toLowerCase() === newField.label.toLowerCase()
          )
        : false;

    if (existsInDefaultFields) {
      toast({
        title: "Field with this name already exists in default fields!",
      });
      return;
    }

    const field = {
      key: `custom_${newField.label}_${Date.now()}_${Math.random()}`, // ✅ FIXED: Truly unique key
      label: newField.label,
      fieldType: newField.fieldType,
    };

    setFormData((prev) => ({
      ...prev,
      customFields: [...prev.customFields, field],
    }));

    setNewField({
      label: "",
      fieldType: "text",
    });

    toast({ title: "Field added successfully!" });
  };

  const handleRemoveField = (fieldId: string) => {
    setFormData((prev) => ({
      ...prev,
      customFields: prev.customFields.filter((field) => field.key !== fieldId),
    }));
  };

  const handleReset = () => {
    const resetData = {
      name: "",
      type: "",
      transactionFee: 0,
      remark: "",
      minAmount: 0,
      maxAmount: 0,
      customFields: [],
    };
    setFormData(resetData);
    form.reset(resetData);
    setNewField({
      label: "",
      fieldType: "text",
    });
  };

  // Handle dialog close
  const handleClose = () => {
    handleReset();
    onClose();
  };

  // Get available field types - use API data if available, fallback to hardcoded options
  const availableFieldTypes = useMemo(() => {
    if (fieldTypes && Array.isArray(fieldTypes)) {
      return fieldTypes.map((type) => ({
        value: type.value.toLowerCase(),
        label: type.key || type.value,
      }));
    }
    return fieldTypeOptions;
  }, [fieldTypes]);

  // Helper function to check if a field is a default field or has protected keys
  const isDefaultField = (fieldKey: string) => {
    // Check if field key is amount or payment_date (protected fields)
    if (fieldKey === "amount" || fieldKey === "payment_date") {
      return true;
    }
    // Keep existing logic for other default fields
    return getDefaultFields.some((field) => field.key === fieldKey);
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {editingMode ? "Edit Payment Mode" : "Add Payment Mode"}
          </DialogTitle>
          <DialogDescription>
            Configure the payment mode settings, transaction fees, and custom
            fields.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Basic Information Form */}
          <div className="space-y-4">
            <h4 className="font-medium">Basic Information</h4>
            <DynamicForm
              fields={formFields}
              form={form}
              onSubmit={handleSubmit}
              onReset={handleClose}
              onFieldChange={handleFieldChange}
              gridColumns={2}
              responsiveColumns={{
                sm: 1,
                md: 2,
              }}
              config={{
                showResetButton: false,
                showSubmitButton: false,
                spacing: "comfortable",
                classes: {
                  input:
                    "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500",
                  textarea:
                    "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-vertical",
                  select:
                    "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white",
                  label: "block text-sm font-medium text-gray-700 mb-1",
                  error: "text-sm text-red-600 mt-1",
                },
              }}
            />
          </div>

          {/* Custom Fields */}
          <div className="space-y-4">
            <h4 className="font-medium">Custom Fields <ContextualHelp title="Custom Fields" content="Define additional fields required for this payment method. For example, credit cards might need card number and expiry date fields, while bank transfers might need account and routing numbers." /> </h4>

            {/* Add New Field */}
            <div className="p-4 border rounded-lg bg-gray-50">
              <h5 className="font-medium mb-3">Add New Field</h5>
              <div className="grid grid-cols-3 gap-3 mb-3">
                <div className="space-y-2">
                  <Label htmlFor="field-label">Field Label <ContextualHelp title="Field Label" content="Enter the label that will be displayed for this field (e.g., 'Card Number', 'Account Number', 'Reference Number'). Make it clear and user-friendly."/>  </Label>
                  <Input
                    id="field-label"
                    value={newField.label}
                    onChange={(e) =>
                      setNewField((prev) => ({
                        ...prev,
                        label: e.target.value,
                      }))
                    }
                    placeholder="Enter field label"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="field-type">Field Type <ContextualHelp title="Field Type" content="Select the appropriate input type: Text for alphanumeric data, Number for numeric values only, or Date for date selection fields." /> </Label>
                  <Select
                    value={newField.fieldType}
                    onValueChange={(value: "text" | "number" | "date") =>
                      setNewField((prev) => ({
                        ...prev,
                        fieldType: value,
                      }))
                    }
                  >
                    <SelectTrigger className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white">
                      <SelectValue placeholder="Select field type" />
                    </SelectTrigger>
                    <SelectContent>
                      {availableFieldTypes.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-end">
                  <Button
                    onClick={handleAddField}
                    // disabled={!newField.label.trim() || !canEdit}
                    className="px-4 py-2 rounded-md font-medium transition-colors"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Add Field
                  </Button>
                </div>
              </div>
            </div>

            {/* Existing Fields */}
            {formData.customFields.length > 0 && (
              <div className="space-y-2">
                <h5 className="font-medium">Configured Fields</h5>
                <div className="space-y-2">
                  {formData.customFields.map((field) => (
                    <div
                      key={field.key}
                      className={`flex items-center justify-between p-3 border rounded-lg`}
                    >
                      <div className="flex items-center gap-3">
                        <div>
                          <span className="font-medium">{field.label}</span>
                          <span className="ml-2 text-sm text-muted-foreground">
                            (
                            {
                              availableFieldTypes.find(
                                (opt) => opt.value === field.fieldType
                              )?.label
                            }
                            )
                          </span>
                        </div>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleRemoveField(field.key)}
                        disabled={isDefaultField(field.key) || !canEdit}
                        className="px-4 py-2 rounded-md font-medium transition-colors"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Custom Form Buttons */}
          <div className="flex justify-end gap-3 pt-6 border-gray-200 mt-8">
            <Button
              variant="outline"
              onClick={handleClose}
              className="px-4 py-2 rounded-md font-medium transition-colors"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              className="px-4 py-2 rounded-md font-medium transition-colors"
            >
              {editingMode ? "Update" : "Add"} Mode
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
